import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DeleteOutlined } from '@ant-design/icons';
import Cookies from 'universal-cookie';
import { CustomerPreferencesHelper } from '../../Helpers/CustomerPreferencesHelper';
import { PaymentConfiguration } from '../../Types/PaymentConfiguration';
import { WebApiMemberDtoMemberDto, WebApiModelsView, WebApiModelsMemberField, WebApiModelsViewColumn } from '../../Repository/eventbookyapi';
import { mapDynamicInputLabel } from '../../Mappers/mapDynamicInputLabel';
import { MemberFieldTypes } from "../../Types/MemberFieldTypes";
import { UiControlType } from "../../Types/UiControlType";
import { sortByUiControlType } from "../../Helpers/TableSortHelper";
import { formatDateString, getDateStringFromDate } from '../../Helpers/DateHelpers';
import { MemberService } from '../../Services/MemberService';
import { TextService } from '../../Services/TextService';
import MemberEditModal from '../Users/MemberEditModal';
import ManagePayments from '../Users/ManagePayments/ManagePayments';
import { useNavigate } from 'react-router-dom';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';

export interface CustomTableProps {
    view: WebApiModelsView;
    members: WebApiMemberDtoMemberDto[];
    memberFields: WebApiModelsMemberField[];
    user?: any;
    membershipProducts?: any[];
    groups?: any[];
    loadMembers?: () => void;
    enableRowClick?: boolean;
    pageSizePreference?: number;
    handlePageSizeChanged?: (newPageSize: number) => void;
    hasPingPaymentIntegration?: boolean;
    hasFortnoxIntegration?: boolean;
    allowCreateFortnoxInvoice?: boolean;
}

export const CustomTable = (props: CustomTableProps) => {
    const navigate = useNavigate();
    const GetText = TextService.GetText;
    const [memberToDelete, setMemberToDelete] = useState<number>(0);

    const onPaginateChange = (value: any, newPageSize: any) => {
        CustomerPreferencesHelper.setPageSize(newPageSize);
        props.handlePageSizeChanged?.(newPageSize);
    };

    const onDeleteClick = (memberId: number) => {
        setMemberToDelete(memberId);
    };

    const handleOk = async () => {
        await MemberService.DeleteMember(memberToDelete);
        setMemberToDelete(0);
        if (props.loadMembers) {
            props.loadMembers();
        }
    };

    // Sort records by column value
    const sortByColumn = (viewColumn: WebApiModelsViewColumn, a: WebApiMemberDtoMemberDto, b: WebApiMemberDtoMemberDto): number => {
        const aValue = getCellValue(viewColumn, a);
        const bValue = getCellValue(viewColumn, b);

        // Determine UI control type
        let uiControl: UiControlType | undefined;

        // Date fields (including JoinedDate)
        if (viewColumn.columnType === MemberFieldTypes.JoinedDate) {
            uiControl = UiControlType.Date;
        }
        // Custom fields - get UI control from field settings
        else if (viewColumn.columnType === MemberFieldTypes.Custom && viewColumn.customFieldId) {
            const field = props.memberFields.find(f => f.id === viewColumn.customFieldId);
            uiControl = field?.uiControl;
        }

        return sortByUiControlType(uiControl, aValue, bValue);
    };

    // Get raw cell value for sorting
    const getCellValue = (viewColumn: WebApiModelsViewColumn, record: WebApiMemberDtoMemberDto): any => {
        const columnType = viewColumn.columnType as any;
        const customFieldId = viewColumn.customFieldId;

        switch (columnType) {
            case MemberFieldTypes.UniqueId:
                return record.uniqueId;
            case MemberFieldTypes.FirstName:
                return record.firstName;
            case MemberFieldTypes.LastName:
                return record.lastName;
            case MemberFieldTypes.Email:
                return record.email;
            case MemberFieldTypes.PhoneNumber:
                return record.phoneNumber;
            case MemberFieldTypes.PersonalNumber:
                return record.personalNumber;
            case MemberFieldTypes.Address:
                return record.address;
            case MemberFieldTypes.PostalCode:
                return record.postalCode;
            case MemberFieldTypes.PostalArea:
                return (record as any).postalArea || '';
            case MemberFieldTypes.Country:
                return record.country;
            case MemberFieldTypes.PhoneNumber2:
                return (record as any).phoneNumber2 || '';
            case MemberFieldTypes.AdressRow2:
                return (record as any).addressRow2 || '';
            case MemberFieldTypes.AdressRow3:
                return (record as any).addressRow3 || '';
            case MemberFieldTypes.Title:
                return (record as any).title || '';
            case MemberFieldTypes.Company:
                return (record as any).company || '';
            case MemberFieldTypes.Extra1:
                return (record as any).extra1 || '';
            case MemberFieldTypes.Extra2:
                return (record as any).extra2 || '';
            case MemberFieldTypes.Extra3:
                return (record as any).extra3 || '';
            case MemberFieldTypes.JoinedDate:
                return (record as any).joinedDate || '';
            case MemberFieldTypes.WebPage:
                return (record as any).webPage || '';
            case MemberFieldTypes.Group:
                return (record as any).groupNames ? (record as any).groupNames.join(', ') : '';
            case MemberFieldTypes.Custom:
                if (!customFieldId) return '';
                if ((record as any).customFieldValues) {
                    const customFieldValue = (record as any).customFieldValues.find((cf: any) => cf.fieldId === customFieldId);
                    if (customFieldValue && customFieldValue.fieldValue !== undefined && customFieldValue.fieldValue !== null) {
                        return customFieldValue.fieldValue;
                    }
                }
                return '';
            default:
                return '';
        }
    };

    // Build columns based on view configuration
    const buildColumns = (): ColumnsType<WebApiMemberDtoMemberDto> => {
        if (!props.view.viewColumns) return [];

        // Sort columns by sortOrder
        const sortedColumns = [...props.view.viewColumns].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        const columns: ColumnsType<WebApiMemberDtoMemberDto> = sortedColumns.map((viewColumn: WebApiModelsViewColumn) => {
            // Get the field name using mapDynamicInputLabel
            const fieldName = mapDynamicInputLabel(
                viewColumn.columnType as any,
                props.memberFields,
                viewColumn.customFieldId || undefined
            );

            // Build column configuration
            const column: any = {
                title: fieldName || 'Unknown',
                key: `${viewColumn.columnType}_${viewColumn.customFieldId || 0}_${viewColumn.sortOrder}`,
                dataIndex: `col_${viewColumn.columnType}_${viewColumn.customFieldId || 0}`,
                render: (text: any, record: WebApiMemberDtoMemberDto) => {
                    return renderCellValue(viewColumn, record);
                },
                sorter: (a: WebApiMemberDtoMemberDto, b: WebApiMemberDtoMemberDto) => {
                    return sortByColumn(viewColumn, a, b);
                }
            };

            return column;
        });

        // Add actions column at the end
        columns.push({
            title: GetText("MemberList_ActionColumn") || 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 150,
            onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
            render: (_: any, record: WebApiMemberDtoMemberDto) => (
                <Space size="middle">
                    <MemberEditModal
                        user={props.user}
                        membershipProducts={props.membershipProducts ?? []}
                        memberFieldSettings={props.memberFields ?? []}
                        loadMembers={props.loadMembers ?? (() => { })}
                        members={props.members ?? []}
                        familyMembers={(props.members ?? []).filter((m: WebApiMemberDtoMemberDto) => m.primaryMemberId === record.memberId)}
                        groups={props.groups}
                        member={record}
                        hasPingPaymentIntegration={props.hasPingPaymentIntegration}
                    />
                    <ManagePayments
                        membershipProducts={props.membershipProducts ?? []}
                        loadMembers={props.loadMembers ?? (() => { })}
                        member={record}
                        hasPingPaymentIntegration={props.hasPingPaymentIntegration}
                        hasFortnoxIntegration={props.hasFortnoxIntegration}
                        allowCreateFortnoxInvoice={props.allowCreateFortnoxInvoice}
                        hasYearlyPayments={props.user?.paymentConfiguration === PaymentConfiguration.YearlyPayments}
                    />
                    {!props.user?.hasOnlyReadRights && (
                        <DeleteOutlined
                            data-testid="delete-member-button"
                            style={{ color: "red", fontSize: "20px" }}
                            onClick={() => onDeleteClick(record?.memberId!)}
                        />
                    )}
                    <Popconfirm
                        title={GetText("MemberList_DeleteMemberConfirmMessage")}
                        key={record.email}
                        open={record.memberId === memberToDelete}
                        placement="topRight"
                        onConfirm={handleOk}
                        onCancel={() => setMemberToDelete(0)}
                    />
                </Space>
            ),
        });

        return columns;
    };

    // Render cell value based on column configuration
    const renderCellValue = (viewColumn: WebApiModelsViewColumn, record: WebApiMemberDtoMemberDto): any => {
        const columnType = viewColumn.columnType as any;
        const customFieldId = viewColumn.customFieldId;

        // Map using MemberFieldTypes enum
        switch (columnType) {
            case MemberFieldTypes.UniqueId:
                return record.uniqueId;
            case MemberFieldTypes.FirstName:
                return record.firstName;
            case MemberFieldTypes.LastName:
                return record.lastName;
            case MemberFieldTypes.Email:
                return record.email;
            case MemberFieldTypes.PhoneNumber:
                return record.phoneNumber;
            case MemberFieldTypes.PersonalNumber:
                return record.personalNumber;
            case MemberFieldTypes.Address:
                return record.address;
            case MemberFieldTypes.PostalCode:
                return record.postalCode;
            case MemberFieldTypes.PostalArea:
                return (record as any).postalArea || '';
            case MemberFieldTypes.Country:
                return record.country;
            case MemberFieldTypes.PhoneNumber2:
                return (record as any).phoneNumber2 || '';
            case MemberFieldTypes.AdressRow2:
                return (record as any).addressRow2 || '';
            case MemberFieldTypes.AdressRow3:
                return (record as any).addressRow3 || '';
            case MemberFieldTypes.Title:
                return (record as any).title || '';
            case MemberFieldTypes.Company:
                return (record as any).company || '';
            case MemberFieldTypes.Extra1:
                return (record as any).extra1 || '';
            case MemberFieldTypes.Extra2:
                return (record as any).extra2 || '';
            case MemberFieldTypes.Extra3:
                return (record as any).extra3 || '';
            case MemberFieldTypes.JoinedDate:
                return (record as any).joinedDate ? getDateStringFromDate((record as any).joinedDate).trim() : '';
            case MemberFieldTypes.WebPage:
                return (record as any).webPage || '';
            case MemberFieldTypes.Group:
                if ((record as any).groupNames && Array.isArray((record as any).groupNames)) {
                    return (record as any).groupNames.map((groupName: string, i: number) => (
                        <span key={i}>
                            {groupName}
                            {i < (record as any).groupNames.length - 1 && <br />}
                        </span>
                    ));
                }
                return '';
            case MemberFieldTypes.Custom:
                if (!customFieldId) {
                    return '';
                }
                if ((record as any).customFieldValues) {
                    const customFieldValue = (record as any).customFieldValues.find((cf: any) => cf.fieldId === customFieldId);
                    if (customFieldValue && customFieldValue.fieldValue !== undefined && customFieldValue.fieldValue !== null) {
                        // Check if this is a date field
                        const field = props.memberFields.find(f => f.id === customFieldId);
                        if (field && field.uiControl === UiControlType.Date) {
                            return getDateStringFromDate(customFieldValue.fieldValue).trim();
                        }
                        if (field && field.uiControl === UiControlType.Number) {
                            return Number(customFieldValue.fieldValue).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US');
                        }
                        if (field && field.uiControl === UiControlType.Currency) {
                            return `${Number(customFieldValue.fieldValue).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${TextService.GetText("Common_CurrencySymbol")}`;
                        }
                        if (field && field.uiControl === UiControlType.Decimal) {
                            return Number(customFieldValue.fieldValue).toLocaleString(getCurrentLanguage() === 'se' ? 'sv-SE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        }
                        return customFieldValue.fieldValue;
                    }
                }
                return '';
            default:
                return '';
        }
    };

    const columns = buildColumns();

    const handleRowClick = (record: WebApiMemberDtoMemberDto) => {
        if (props.enableRowClick) {
            navigate(`/${getCurrentLanguage()}/users/${record.memberId}`);
        }
    };

    return (
        <Table
            columns={columns}
            dataSource={(props.members ?? []).filter((m: WebApiMemberDtoMemberDto) => m.primaryMemberId == null)}
            rowKey="uniqueId"
            scroll={{ x: 'max-content' }}
            pagination={{
                pageSize: props.pageSizePreference || 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                position: ['topRight'],
                locale: { items_per_page: "/ " + GetText("Common_Page") },
                onShowSizeChange: onPaginateChange
            }}
            onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: props.enableRowClick ? { cursor: 'pointer' } : {}
            })}
        />
    );
};

export default CustomTable;
